import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Button,
} from "@mui/material";
import moment from "moment";
import styles from "../../styles";
import { getApiData } from "../../../../utils/APIHelper";
import { isEmpty } from "lodash";
import Permission from "../../../../components/Permission";
import UpdateDetails from "./ProjectUpdateDetails/UpdateDetails";

export default function ProjectUpdates({
  projectId,
  projectDetails,
  isMobile,
  styles: stylesProp,
  subTab,
}) {
  const [loader, setLoader] = useState(false);
  const [details, setDetails] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const updateId = searchParams.get("updateId");
  const isDetailsScreen = subTab === "details" || !!updateId;

  const isMobileDevice = isMobile;
  const componentStyles = stylesProp || styles(isMobileDevice);

  useEffect(() => {
    if (projectId) {
      getProjectUpdates();
    }
  }, [projectId]);

  async function getProjectUpdates() {
    setLoader(true);
    try {
      const response = await getApiData(
        `api/project/update-summary?project_id=${projectId}`,
        "GET"
      );
      if (response) {
        setDetails(response?.data || []);
      }
    } catch (error) {
      console.log("error:===>>>", error);
    } finally {
      setLoader(false);
    }
  }

  const detailsPath = `/project/details/${id}/project-updates/details`;

  useEffect(() => {
    if (updateId && subTab !== "details" && id) {
      const params = new URLSearchParams(window.location.search);
      if (projectId) params.set("project_id", String(projectId));
      navigate(`${detailsPath}?${params.toString()}`, { replace: true });
    }
  }, [updateId, subTab, id, projectId, detailsPath, navigate]);

  const handleSelectRow = (row) => {
    if (row?.update_id) {
      const params = new URLSearchParams({
        updateId: String(row.update_id),
        ...(row.update_date && { updateDate: row.update_date }),
      });
      if (projectId) params.set("project_id", String(projectId));
      navigate(`${detailsPath}?${params.toString()}`, { replace: true });
    }
  };

  if (isDetailsScreen) {
    return (
      <UpdateDetails
        projectId={projectId}
        projectDetails={projectDetails}
        isMobile={isMobile}
        styles={componentStyles}
        listData={details}
        refetchList={getProjectUpdates}
        detailsPath={detailsPath}
      />
    );
  }

  return (
    <>
      <Permission
        resource="MILESTONE_UPDATES"
        action="READ"
        fallback={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "256px",
            }}
          >
            <p style={{ color: "#757575", margin: 0 }}>
              You don't have permission to view project updates.
            </p>
          </div>
        }
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            height: "100%",
          }}
        >
          <Permission resource="MILESTONE_UPDATES" action="CREATE">
            <div style={{ margin: "2vh .5vw 0vh .5vw" }}>
              <Button
                variant="contained"
                onClick={() => {
                  const params = new URLSearchParams({ updateId: "new" });
                  if (projectId) params.set("project_id", String(projectId));
                  navigate(`${detailsPath}?${params.toString()}`);
                }}
                sx={{
                  backgroundColor: "#4D6BF6",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: 500,
                  boxShadow: "none",
                  color: "#fff",
                  border: "none",
                  padding: ".7vh 1vw",
                  cursor: "pointer",
                  fontFamily: "Inter Tight",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "#4D6BF6",
                    border: "none",
                  },
                }}
              >
                Add Progress Update
              </Button>
            </div>
          </Permission>

          {loader ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "24px",
              }}
            >
              <CircularProgress />
            </div>
          ) : !isEmpty(details) ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "32px",
                margin: "1vh .5vw",
              }}
            >
              {details?.map((item) => (
                <Card
                  key={item.update_id}
                  onClick={() => handleSelectRow(item)}
                  sx={{
                    borderRadius: "12px",
                    cursor: "pointer",
                    boxShadow: 3,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: 6 },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={
                      item?.image?.path ||
                      "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                    }
                    alt={item.update_name}
                    sx={{ objectFit: "cover", boxShadow: "none !important" }}
                  />
                  <CardContent
                    sx={{
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      boxShadow: "none !important",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#0D0D0E",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.update_name}
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#949DAA",
                        margin: 0,
                      }}
                    >
                      {item.update_date
                        ? moment(item.update_date).format("MMM DD, YYYY")
                        : "-"}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div
              style={{
                marginTop: "16px",
                textAlign: "center",
                color: "#757575",
              }}
            >
              <p style={{ margin: 0 }}>No progress updates available.</p>
            </div>
          )}
        </div>
      </Permission>
    </>
  );
}
